import GenericBarGraph from "@/components/Graph/GenericBarGraph";
import GenericDonutChart from "@/components/Graph/GenericDonutChart.";
import Table from "@/components/Table/Table";
import { Box } from "@mui/system";
import React from "react";
import { SectionHeader } from "./SectionHeader";
import GenericLineChart from "@/components/Graph/GenericLineChart";
import GenericPieChart from "@/components/Graph/GenericPieChart";
import ExtremeValueGrid from "./ExtreamValueGrid";
import LatestOpportunityGrid from "./LatestOpportunityGrid";
import { ChartBox } from "./ChartBox";
import type { OpportunityMetricsProps } from "@/types/dashboardtypes";

export default function OpportunityMetrics(props: OpportunityMetricsProps) {
  const {
    visitLabels,
    visitDatasets,
    callsLabels,
    callsDatasets,
    roasterLabels,
    roasterDatasets,
    VisitObjectiveTotalCountLabels,
    VisitObjectiveTotalCountDatasets,
    CallObjectiveTotalCountLabels,
    CallObjectiveTotalCountDatasets,
    VisitStatusCountLabels,
    VisitStatusCountDatasets,
    CallLogStatusCountLabels,
    CallLogStatusCountDatasets,
    upcomingVisitsColumn,
    Data,
    columns,
    opportunityFollowUpsColumns,
    lostReasonColumns,
    WonAndLostOpportunityLabels,
    WonAndLostOpportunityDatasets,
    CurrentOpportunityInStagesLabels,
    CurrentOpportunityInStagesDatasets,
    DueOpportunityInStagesLabels,
    DueOpportunityInStagesDatasets,
    OpportunitiesCountLabels,
    OpportunitiesCountDatasets,
    AllOpportunityTagsLabels,
    AllOpportunityTagsDatasets,
    WonOpportunityTagsLabels,
    WonOpportunityTagsDatasets,
    LostOpportunityTagsLabels,
    LostOpportunityTagsDatasets,
    MaxMinOpportunityData,
    LatestOpportunity,
    LastUpdatedSinces,
    singleColumn,
  } = props;

  const gridColumns = singleColumn ? "1fr" : "49% 49%";

  return (
    <>
      {/* Bar Charts */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridColumns,
          gap: "2%",
        }}
      >
        <GenericBarGraph
          title="Total Roaster Plan vs Visited"
          labels={visitLabels}
          datasets={visitDatasets}
        />
        <GenericBarGraph
          title="Total Calls vs Success Calls"
          labels={callsLabels}
          datasets={callsDatasets}
        />
        <GenericBarGraph
          title="Total Roster Planned vs Visited"
          labels={roasterLabels}
          datasets={roasterDatasets}
        />
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            p: 2,
            boxShadow: 1,
          }}
        >
          <ChartBox title="List Of Upcoming Visit Scheduled">
            <Table
              columns={upcomingVisitsColumn}
              data={Data?.data?.metricsData?.UpcommingVisitDetails || []}
            ></Table>
          </ChartBox>
        </Box>
        <GenericBarGraph
          title="Most Purpose Of Visit & Visit Count"
          labels={VisitObjectiveTotalCountLabels}
          datasets={VisitObjectiveTotalCountDatasets}
        />
        <GenericBarGraph
          title="Most Call Objective & Call Count"
          labels={CallObjectiveTotalCountLabels}
          datasets={CallObjectiveTotalCountDatasets}
        />
      </Box>

      {/* Visit / Call Doughnut */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridColumns,
          gap: "2%",
          justifyContent: "space-between",
          mt: 6,
        }}
      >
        <GenericDonutChart
          title="Visit Status & Count"
          labels={VisitStatusCountLabels}
          datasets={VisitStatusCountDatasets}
        />
        <GenericDonutChart
          title="Call Status & Count"
          labels={CallLogStatusCountLabels}
          datasets={CallLogStatusCountDatasets}
        />
      </Box>

      {/* Visit Table */}
      <Box sx={{ mt: 2, borderRadius: 2, overflow: "hidden", boxShadow: 1 }}>
        <SectionHeader title="Recent 10 Visits" />
        <Box sx={{ bgcolor: "#fff", p: 1 }}>
          <Table
            columns={columns}
            data={Data?.data?.metricsData?.LatestVisitsDetails || []}
          />
        </Box>
      </Box>
      {/* Pipeline Metrics */}
      <Box sx={{ mt: 2, borderRadius: 2, overflow: "hidden", boxShadow: 1 }}>
        <SectionHeader title="Pipeline Metrics" />
        <GenericLineChart
          title={"Deal Won And Lost Percentage"}
          datasets={WonAndLostOpportunityDatasets}
          labels={WonAndLostOpportunityLabels}
        ></GenericLineChart>
      </Box>

      {/* Opportunity Follow-Ups */}
      <Box sx={{ mt: 2, borderRadius: 2, overflow: "hidden", boxShadow: 1 }}>
        <SectionHeader title="Opportunity Follow-Ups" />
        <Box sx={{ bgcolor: "#fff", p: 1 }}>
          <Table
            columns={opportunityFollowUpsColumns}
            data={Data?.data?.opportunityData?.FollowUpDetails || []}
          />
        </Box>
      </Box>

      {/* Opportunity Stages Overview */}
      <Box sx={{ mt: 2, borderRadius: 2, overflow: "hidden", boxShadow: 1 }}>
        <SectionHeader title="Opportunity Stages Overview" />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridColumns,
            gap: "2%",
            justifyContent: "space-between",
            p: 2,
            alignItems: "center",
          }}
        >
          <GenericPieChart
            datasets={CurrentOpportunityInStagesDatasets}
            labels={CurrentOpportunityInStagesLabels}
            title={"Current Opportunity"}
          ></GenericPieChart>
          <GenericPieChart
            datasets={DueOpportunityInStagesDatasets}
            labels={DueOpportunityInStagesLabels}
            title={"Due Opportunity"}
          ></GenericPieChart>
        </Box>
      </Box>
      {/* Opportunity Creation Trends */}
      <Box
        sx={{
          mt: 2,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 1,
        }}
      >
        <SectionHeader title="Opportunity Creation Trends" />
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            p: 2,
            boxShadow: 1,
          }}
        >
          <ChartBox title={"Lost Reason vs Count & Value"}>
            <Table
              data={Data?.opportunitymetricsData?.TopLostReason || []}
              columns={lostReasonColumns}
            ></Table>
          </ChartBox>
        </Box>
      </Box>
      {/* Top Opportunity Lost Reason */}
      <Box
        sx={{
          mt: 2,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 1,
        }}
      >
        <SectionHeader title="Top Opportunity Lost Reason" />
        <GenericBarGraph
          datasets={OpportunitiesCountDatasets}
          labels={OpportunitiesCountLabels}
          title={"Opportunity Creation Trends"}
          height={200}
        ></GenericBarGraph>
      </Box>

      {/* === Top Opportunity Tags === */}
      <Box
        sx={{
          mt: 2,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 1,
        }}
      >
        <SectionHeader title="Top Opportunity Tags" />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: singleColumn ? "1fr" : { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            mt: 2,
          }}
        >
          <GenericBarGraph
            datasets={AllOpportunityTagsDatasets}
            labels={AllOpportunityTagsLabels}
            title={"Tag vs Count & Value"}
            height={220}
          />

          <GenericBarGraph
            datasets={WonOpportunityTagsDatasets}
            labels={WonOpportunityTagsLabels}
            title={"Won Tag vs Count & Value"}
            height={220}
          />

          <GenericBarGraph
            datasets={LostOpportunityTagsDatasets}
            labels={LostOpportunityTagsLabels}
            title={"Lost Tag vs Count & Value"}
            height={220}
          />
        </Box>
      </Box>

      {/* === Opportunity Value Extreme === */}
      <ExtremeValueGrid
        title="Opportunity Value Extreme"
        singleColumn={!!singleColumn}
        data={[
          {
            title: "Opportunity Value",
            max: {
              oppNo: MaxMinOpportunityData?.MaxOpportunityNumber,
              name: MaxMinOpportunityData?.MaxOpportunityName,
              value: MaxMinOpportunityData?.MaxValue,
              daysAgo: MaxMinOpportunityData?.MaxLastUpdatedSince,
            },
            min: {
              oppNo: MaxMinOpportunityData?.MinOpportunityNumber,
              name: MaxMinOpportunityData?.MinOpportunityName,
              value: MaxMinOpportunityData?.MinValue,
              daysAgo: MaxMinOpportunityData?.MinLastUpdatedSince,
            },
          },
          {
            title: "Won Opportunity Value",
            max: {
              oppNo: MaxMinOpportunityData?.MaxWonOpportunityNumber,
              name: MaxMinOpportunityData?.MaxWonOpportunityName,
              value: MaxMinOpportunityData?.MaxWonValue,
              daysAgo: MaxMinOpportunityData?.MaxWonLastUpdatedSince,
            },
            min: {
              oppNo: MaxMinOpportunityData?.MinWonOpportunityNumber,
              name: MaxMinOpportunityData?.MinWonOpportunityName,
              value: MaxMinOpportunityData?.MinWonValue,
              daysAgo: MaxMinOpportunityData?.MinWonLastUpdatedSince,
            },
          },
          {
            title: "Lost Opportunity Value",
            max: {
              oppNo: MaxMinOpportunityData?.MaxLostOpportunityNumber,
              name: MaxMinOpportunityData?.MaxLostOpportunityName,
              value: MaxMinOpportunityData?.MaxLostOpportunityValue,
              daysAgo: MaxMinOpportunityData?.MaxLostLastUpdatedSince,
            },
            min: {
              oppNo: MaxMinOpportunityData?.MinLostOpportunityNumber,
              name: MaxMinOpportunityData?.MinLostOpportunityName,
              value: MaxMinOpportunityData?.MinLostOpportunityValue,
              daysAgo: MaxMinOpportunityData?.MinLostLastUpdatedSince,
            },
          },
        ]}
      />

      {/* === Latest Opportunity Details === */}
      <LatestOpportunityGrid
        title="Latest Opportunity Details"
        singleColumn={!!singleColumn}
        data={[
          {
            leftTitle: "Latest Opportunity Created",
            rightTitle: "Latest Opportunity Modified",
            left: {
              oppNo: LatestOpportunity?.CreatedOpportunityNumber,
              name: LatestOpportunity?.CreatedOpportunityName,
              tag: LatestOpportunity?.CreatedOpportunityTags,
              value: LatestOpportunity?.CreatedOpportunityValue,
              daysAgo: LastUpdatedSinces?.AllStageLastUpdatedSince,
            },
            right: {
              oppNo: LatestOpportunity?.ModifiedOpportunityNumber,
              name: LatestOpportunity?.ModifiedOpportunityName,
              tag: LatestOpportunity?.ModifiedOpportunityTags,
              value: LatestOpportunity?.ModifiedOpportunityValue,
              daysAgo: LastUpdatedSinces?.AllStageLastUpdatedSince,
            },
          },
          {
            leftTitle: "Latest Opportunity Won",
            rightTitle: "Latest Opportunity Lost",
            left: {
              oppNo: LatestOpportunity?.WonOpportunityNumber,
              name: LatestOpportunity?.WonOpportunityName,
              tag: LatestOpportunity?.WonOpportunityTags,
              value: LatestOpportunity?.WonOpportunityValue,
              daysAgo: LatestOpportunity?.WonLostLastUpdatedSince,
            },
            right: {
              oppNo: LatestOpportunity?.LostOpportunityNumber,
              name: LatestOpportunity?.LostOpportunityName,
              tag: LatestOpportunity?.LostOpportunityTags,
              value: LatestOpportunity?.LostOpportunityValue,
              daysAgo: LastUpdatedSinces?.LostLastUpdatedSince,
            },
          },
        ]}
      />
    </>
  );
}
